import { useState } from "react";
import { useParams } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Card, MessageBar, MessageBarBody, Spinner, Switch, Text, tokens } from "@fluentui/react-components";
import { Link, useNavigate } from "react-router";
import { ConfirmButton } from "../components/ConfirmButton.tsx";
import { AuthCard } from "../components/AuthCard.tsx";
import { DayChips } from "../components/DayChips.tsx";
import { SavePage } from "../components/SavePage.tsx";
import { ClientWeeks, type PortalWeek } from "../components/ClientWeeks.tsx";
import { CreateClientInput, type ClientPreferenceInput } from "@gymplan/shared";
import { Field, Input, ToggleButton } from "@fluentui/react-components";
import { api, type AvailabilitySlot, type Client } from "../lib/api.ts";

interface Portal {
  timezone: string;
  weeks: PortalWeek[];
  client: Client & { whatsappOptIn: boolean };
  consent: { whatsapp: { granted: boolean; createdAt: string } | null };
  trainerName: string;
  sessionLengthMinutes: number;
  gyms: { id: string; name: string; isPrimary: boolean }[];
  availability: AvailabilitySlot[];
}

/** Client's own page, reached via their personal magic link. No password needed. */
export function ClientPortalPage() {
  const { token } = useParams();
  const q = useQuery({ queryKey: ["portal", token], queryFn: () => api.get<Portal>(`/api/clients/portal/${token}`), retry: false });
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const nav = useNavigate();
  const [deleted, setDeleted] = useState(false);
  const [askSave, setAskSave] = useState(0);
  const optIn = useMutation({ mutationFn: (on: boolean) => api.patch(`/api/clients/portal/${token}`, { whatsappOptIn: on }), onSuccess: () => void q.refetch() });
  const erase = useMutation({ mutationFn: () => api.del(`/api/clients/portal/${token}`), onSuccess: () => setDeleted(true) });
  const save = useMutation({
    mutationFn: (v: CreateClientInput) => api.patch<Client>(`/api/clients/portal/${token}`, v),
    onSuccess: () => {
      setSavedAt(Date.now());
      void q.refetch();
    },
  });

  if (deleted) return <AuthCard title="Your data has been deleted" subtitle="Your trainer has been told. Thanks for training with RackUp."><span /></AuthCard>;
  if (q.isLoading) return <AuthCard title="Loading…"><Spinner /></AuthCard>;
  if (q.isError || !q.data) return <AuthCard title="Link not valid" subtitle="Ask your trainer to send you a fresh link."><span /></AuthCard>;
  const { client, trainerName, sessionLengthMinutes, availability } = q.data;
  void nav;

  return (
    <AuthCard title={`Hi ${client.name.split(" ")[0]}`} subtitle={`Your training with ${trainerName} · ${sessionLengthMinutes}-minute sessions`} wide xwide>
      <SavePage token={token!} trainer={trainerName.split(" ")[0] ?? trainerName} reopen={askSave} />
      <ClientWeeks weeks={q.data.weeks} timezone={q.data.timezone} trainer={trainerName.split(" ")[0] ?? trainerName} preferences={client.preferences} />
      {savedAt && (
        <MessageBar intent="success">
          <MessageBarBody>Saved. {trainerName} will see your updated times.</MessageBarBody>
        </MessageBar>
      )}
      <PortalForm
        key={q.dataUpdatedAt}
        availability={availability}
        initial={{
          name: client.name,
          phone: client.phone,
          email: client.email ?? "",
          sessionsPerWeek: client.sessionsPerWeek,
          preferences: client.preferences.map((p) => ({ weekday: p.weekday as never, startTime: p.startTime ?? undefined, endTime: p.endTime ?? undefined, gymIds: [] })),
        }}
        onSubmit={(v) => save.mutateAsync(v).then(() => undefined)}
        busy={save.isPending}
      />
      <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        <Text weight="semibold">Messages & your data</Text>
        <Switch label="WhatsApp messages about my availability and sessions" checked={client.whatsappOptIn} onChange={(_, d) => optIn.mutate(d.checked)} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button as="a" href={`/api/clients/portal/${token}/export`} appearance="secondary" size="small">Download my data</Button>
          <ConfirmButton size="small" appearance="outline" title="Delete your data?" message={`This removes your details, preferences and messages from RackUp and cancels upcoming sessions. ${trainerName} will be told. This can't be undone.`} confirmLabel="Delete my data" onConfirm={() => erase.mutate()}>Delete my data</ConfirmButton>
        </div>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          This link is personal to you — don't share it.{" "}
          <Button appearance="transparent" size="small" style={{ padding: 0, minWidth: 0, height: "auto", verticalAlign: "baseline" }} onClick={() => setAskSave((n) => n + 1)}>
            Save this page
          </Button>{" "}
          · <Link to="/privacy" target="_blank">Privacy notice</Link>.
        </Text>
      </Card>
    </AuthCard>
  );
}

function PortalForm({ initial, availability, onSubmit, busy }: { initial: { name: string; phone: string; email: string; sessionsPerWeek: number; preferences: ClientPreferenceInput[] }; availability: AvailabilitySlot[]; onSubmit: (v: CreateClientInput) => Promise<void>; busy: boolean }) {
  const [f, setF] = useState(initial);
  const [err, setErr] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = CreateClientInput.safeParse({ ...f, phone: f.phone.replace(/\s+/g, ""), email: f.email || undefined });
    if (!parsed.success) return setErr(parsed.error.issues[0]?.message ?? "Please check the form.");
    setErr(null);
    await onSubmit(parsed.data).catch((ex) => setErr((ex as Error).message));
  };
  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {err && (
        <MessageBar intent="error">
          <MessageBarBody>{err}</MessageBarBody>
        </MessageBar>
      )}
      <Field label="Name" required>
        <Input size="large" value={f.name} onChange={(_, d) => setF({ ...f, name: d.value })} />
      </Field>
      <Field label="WhatsApp number" required>
        <Input size="large" type="tel" inputMode="tel" value={f.phone} onChange={(_, d) => setF({ ...f, phone: d.value })} />
      </Field>
      <Field label="Email (optional)">
        <Input size="large" type="email" inputMode="email" value={f.email} onChange={(_, d) => setF({ ...f, email: d.value })} />
      </Field>
      <Field label="Sessions a week">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 6 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <ToggleButton key={n} size="large" style={{ minWidth: 0 }} checked={f.sessionsPerWeek === n} onClick={() => setF({ ...f, sessionsPerWeek: n })}>
              {n}
            </ToggleButton>
          ))}
        </div>
      </Field>
      <Text weight="semibold">When you can train</Text>
      <DayChips value={f.preferences} onChange={(preferences) => setF({ ...f, preferences })} availability={availability} />
      <Button size="large" appearance="primary" type="submit" disabled={busy}>
        Save my details
      </Button>
    </form>
  );
}
